import { useEffect, type ReactNode } from "react";
import { Redirect, useLocation, useSearch } from "wouter";
import { useUser } from "@/hooks/useUser";
import { useAuthModal } from "@/components/auth/AuthModalContext";
import { PageLoader } from "@/components/ui/spinner";
import { AlertTriangle, RefreshCw, LogOut } from "lucide-react";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { browserAuthReturnPath, resolveAuthReturnPath } from "@/lib/safeRedirect";
import { guestProfileCompletionFor } from "@/lib/guest-profile-completion";
import { GuestProfileCompletionBoundary } from "@/components/checkout/GuestBookingAccount";

interface RequireAuthProps {
  allowCheckoutGuest?: boolean;
  children: ReactNode;
  adminOnly?: boolean;
  requiredRole?: number;
}

export function RequireAuth({ children, adminOnly = false, requiredRole, allowCheckoutGuest = false }: RequireAuthProps) {
  const { user, profile, needsProfile, isLoading, isAdmin, adminRole, profileError, retryProfile, signOut } = useUser();
  const acceptedUser = user && (user.app_metadata?.checkout_guest !== true || allowCheckoutGuest && !adminOnly && requiredRole == null);
  const [location] = useLocation();
  const search = useSearch().replace(/^\?/, "");
  // Wouter's location omits the query. Booking selections live in it, so
  // preserving just "/checkout" silently loses the car, dates and journey.
  const routedNext = `${location}${search ? `?${search}` : ""}`;
  const next = typeof window === "undefined" ? routedNext :
    resolveAuthReturnPath(routedNext, browserAuthReturnPath(window.location, import.meta.env.BASE_URL));
  const { open: openAuthModal } = useAuthModal();
  const { t } = useAppTranslation();

  useEffect(() => {
    if (!isLoading && !acceptedUser) {
      openAuthModal({ next });
    }
  }, [isLoading, acceptedUser, next, openAuthModal]);

  if (isLoading) {
    return (
      <PageLoader className="min-h-[60vh]" />
    );
  }

  if (!acceptedUser) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="max-w-md text-center">
          <h2 className="text-lg font-semibold">{t("auth.sign_in_required")}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t("auth.please_sign_in_to_continue")}</p>
          <button
            type="button"
            onClick={() => openAuthModal({ next })}
            className="primary-action mt-4 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold"
          >
            {t("auth.sign_in")}
          </button>
        </div>
      </div>
    );
  }

  // Render the completion form itself, not protected page content, for the
  // proven post-booking customer. Otherwise the profile guard would keep the
  // nested inline form behind a loader forever. Administrative routes and
  // ordinary signup retain their original guards.
  if (allowCheckoutGuest && !adminOnly && requiredRole == null &&
      (needsProfile || profile?.is_complete === false) && guestProfileCompletionFor(user)) {
    return <GuestProfileCompletionBoundary>{children}</GuestProfileCompletionBoundary>;
  }

  if (user && !profile) {
    if (profileError) {
      return (
        <div className="flex min-h-[60vh] items-center justify-center px-4">
          <div className="max-w-md text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-amber-500" />
            <h2 className="mt-4 text-lg font-semibold">{t("auth.unable_to_load_profile")}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("auth.profile_load_error_body", { defaultValue: "We're having trouble connecting to the server. Please try again." })}
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                onClick={() => retryProfile()}
                className="primary-action inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold"
              >
                <RefreshCw className="h-4 w-4" />
                {t("common.try_again", { defaultValue: "Try again" })}
              </button>
              <button
                onClick={() => signOut()}
                className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-muted"
              >
                <LogOut className="h-4 w-4" />
                {t("auth.sign_out", { defaultValue: "Sign out" })}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <PageLoader className="min-h-[60vh]" />
    );
  }

  if (requiredRole !== undefined && adminRole < requiredRole) {
    return <Redirect to="/" />;
  }

  if (adminOnly && !isAdmin) {
    return <Redirect to="/" />;
  }

  // Hard gate: incomplete profile blocks the page. The global
  // AuthModalProvider's completion interstitial overlays this loader.
  if (profile && profile.is_complete === false) {
    return <PageLoader className="min-h-[60vh]" />;
  }

  return <>{children}</>;
}
